// src/utils/invoiceRefundStatus.js — refundstatus-8a3d6f12 (R1d-3)
// What the invoice screen SAYS about a refund after it is made: where it stands in QuickBooks, and
// whether its credit note was emailed. Display rules only — the records are written by the server
// (organizations/{org}/invoices/{inv}/refunds/{id}, readable by Owner / Admin / Manager only, and
// organizations/{org}/creditNotes/{id}); Retry and Resend are server calls with their own gates.
//
// A reply from Retry or Resend is READ, not assumed: a failure comes back as an ordinary reply
// ({success:false, qbError} / {success:false, emailState, emailError}), not as an error.

const QB_TRY_CAP = 5; // the catch-up gives up after this many failed tries (functions QB_SWEEP_ATTEMPT_CAP)

/**
 * A refund's QuickBooks status, from its record. null = nothing to show (no record).
 * kind: posted | pending | error | givenUp | blocked | notApplicable | cardPending | cardFailed
 */
function refundQbStatus(rec) {
    if (!rec) return null;
    const partlyPosted = !!(rec.qbRefundReceiptId || rec.qbPurchaseId);
    const out = (kind, label, tone, extra = {}) => ({ kind, label, tone, detail: '', note: '', canRetry: false, needsBank: false, canChangeBank: false, ...extra });
    if (rec.state === 'failed') return out('cardFailed', 'Card refund failed', 'red', { detail: rec.failureReason || '' });
    if (rec.state === 'stripePending') return out('cardPending', 'Waiting for Stripe', 'gray');
    const retry = { canRetry: true, canChangeBank: !partlyPosted };
    switch (rec.qbState) {
        case 'posted': return out('posted', 'In QuickBooks', 'green');
        case 'pending':
        case 'posting': return out('pending', 'Sending to QuickBooks…', 'gray');
        case 'blocked': return out('blocked', 'Waiting for a bank account', 'amber', { detail: rec.qbError || '', needsBank: !partlyPosted, ...retry });
        case 'error':
            if (rec.qbExhaustedAt) return out('givenUp', 'QuickBooks gave up', 'red', { detail: rec.qbError || '', note: `Spatchy tried ${QB_TRY_CAP} times and stopped. Fix the problem, then Retry.`, ...retry });
            return out('error', 'QuickBooks error', 'red', { detail: rec.qbError || '', note: `Spatchy will try again by itself (${Math.min(Number(rec.qbAttempts) || 0, QB_TRY_CAP)} of ${QB_TRY_CAP} tries used).`, ...retry });
        case 'notApplicable': return out('notApplicable', 'Not sent to QuickBooks', 'gray');
        default: return null;
    }
}

/**
 * A credit note's email status, from its record. null = nothing to show (record not loaded).
 * kind: sent | failed | noEmail | pending | sending | notRequested
 */
function creditNoteEmailStatus(cn, formatDate) {
    if (!cn) return null;
    const out = (kind, text, tone, action) => ({ kind, text, tone, canSend: !!action, sendLabel: action || '' });
    switch (cn.emailState) {
        case 'sent': {
            const when = cn.emailSentAt && formatDate ? formatDate(cn.emailSentAt) : '';
            return out('sent', `Emailed to ${cn.emailSentTo || 'the customer'}${when ? ` on ${when}` : ''}`, 'green', 'Resend');
        }
        case 'failed': return out('failed', `Email failed: ${cn.emailError || 'the email could not be sent'}`, 'red', 'Resend');
        case 'noEmail': return out('noEmail', 'Not emailed: the customer has no email address', 'amber', 'Send');
        case 'sending': return out('sending', 'Being emailed now', 'gray', null);
        case 'pending': return out('pending', 'Not emailed yet', 'gray', 'Send');
        case 'notRequested': return out('notRequested', 'Not emailed (not requested)', 'gray', 'Send');
        default: return null;
    }
}

/** The Retry request. The bank account is sent only when one was chosen AND it may still be changed. */
function buildRetryPayload({ invoiceId, refundId, bankAccountId, canChangeBank }) {
    return { invoiceId, refundId, ...(canChangeBank && bankAccountId ? { bankAccountId: String(bankAccountId) } : {}) };
}

/** Read Retry's reply: posted, or QuickBooks' own reason word for word. */
function readRetryReply(data) {
    const d = data || {};
    if (d.success === true || d.alreadyPosted === true || d.qbState === 'posted') return { ok: true, message: 'Posted to QuickBooks.' };
    return { ok: false, message: d.qbError || `QuickBooks has not taken it yet (status: ${d.qbState || d.result || 'unknown'}).` };
}

/** Read Resend's reply: sent, or why not. */
function readResendReply(data) {
    const d = data || {};
    if (d.success === true || d.emailState === 'sent') return { ok: true, message: 'Credit note emailed.' };
    if (d.emailState === 'noEmail') return { ok: false, message: 'The customer has no email address.' };
    return { ok: false, message: d.emailError || 'The credit note could not be emailed.' };
}

/** A thrown refusal (permission, "being emailed right now", "partly in QuickBooks"…), word for word. */
const readCallError = (err, fallback) => (err && typeof err.message === 'string' && err.message.trim()) || fallback;

/** Does this invoice carry a refund made by the new flow (so there are refund records to read)? */
const hasNewFlowRefund = (invoice) => !!invoice && Array.isArray(invoice.paymentHistory) && invoice.paymentHistory.some((r) => !!r && !!r.refundId);

/** The ids of this invoice's credit notes, from its payment lines (sorted, no repeats). */
function creditNoteIdsOf(invoice) {
    const rows = invoice && Array.isArray(invoice.paymentHistory) ? invoice.paymentHistory : [];
    return [...new Set(rows.filter((r) => r && r.isCreditNote === true && typeof r.creditNoteId === 'string' && r.creditNoteId && !r.creditNoteId.includes('/')).map((r) => r.creditNoteId))].sort();
}

/** Which bank account the Retry panel starts on: the refund's own, else the Settings default, else none. */
function retryBankPreset(rec, companySettings, banks) {
    const has = (id) => !!id && (banks || []).some((a) => String(a.id) === String(id));
    const own = rec && rec.bankAccountId;
    const preset = companySettings && companySettings.quickbooksSettings && companySettings.quickbooksSettings.refundFromAccountId;
    return has(own) ? String(own) : has(preset) ? String(preset) : '';
}

export { hasNewFlowRefund, creditNoteIdsOf, retryBankPreset, QB_TRY_CAP, refundQbStatus, creditNoteEmailStatus, buildRetryPayload, readRetryReply, readResendReply, readCallError };
